// Full-document render of the Oscar thank-you (order confirmation) view:
// PageSkeleton (the React document shell) wrapping the order intro + the
// Create-Account island, the static GTWO share module, the order-number/help
// title, and the native React OrderDetail summary (typed-serialized
// server-side; its display-only OrderOffer islands render inline). The
// deferred GA4 dataLayer events (ga_ecom + ga_purchase) and Google Seller
// Ratings survey opt-in (ga_seller) inject after hydration via useEffect.
// Header/footer slots are omitted so the default site chrome renders.
import React from "react";
import { DataLayerPush } from "tsi-common-react/src/components/DataLayerPush/index";
import { GASeller } from "tsi-common-react/src/components/GASeller/index";

import { templates } from "@reactivated";

import { OrderDetail } from "../components/OrderDetail";
import { PageSkeleton } from "../components/PageSkeleton";
import { Template as CreateAccount } from "./CreateAccountTemplate";

export const Template = (
    props: templates.ThankYouDocumentTemplate,
): React.JSX.Element => {
    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            <div className="thank-you-container l-capped-width u-margin-bottom--xl u-full-width-title">
                <div className="thank-you">
                    <div className="intro">
                        <h1 className="intro__title">
                            Rest easy, <span>{props.first_name}.</span>{" "}
                            We&apos;re working on your order.
                        </h1>
                        <p className="intro__copy">
                            You&apos;ll receive order confirmation emails at
                            each step of your order&apos;s progress, at{" "}
                            <strong>{props.email}</strong>. Check your order
                            status anytime at{" "}
                            <a
                                className="intro__link"
                                href={props.order_list_url}
                            >
                                TempurPillows.com
                            </a>
                            . Thanks for shopping with us.
                        </p>
                        {/* create_account is always populated on pillows; the
                            shared template types it nullable (cocoon omits it). */}
                        {props.create_account && (
                            <CreateAccount {...props.create_account} />
                        )}
                    </div>

                    <div className="gtwo-module">
                        <div className="gtwo-module__content">
                            <h1 className="gtwo-module__header">
                                Share for a chance to{" "}
                                <span className="highlight">WIN</span>
                            </h1>
                            <div className="gtwo-module__icon-content">
                                <div className="gtwo-module__icon-container">
                                    <img
                                        className="gtwo-module__icon"
                                        src={props.fb_icon_url}
                                        loading="lazy"
                                        decoding="async"
                                    />
                                    <img
                                        className="gtwo-module__icon"
                                        src={props.twitter_icon_url}
                                        loading="lazy"
                                        decoding="async"
                                    />
                                </div>
                            </div>
                            <div className="gtwo-module__copy">
                                Tell everyone how excited you are to try out
                                your new Tempur-Pedic<sup>&reg;</sup> Pillow and
                                be entered for a chance to{" "}
                                <span className="highlight">WIN $1,000!</span>
                            </div>
                            <div className="gtwo-module__cta-button-section">
                                <a
                                    className="button button--inverse gtwo-module__cta-button"
                                    href="https://share.tempurpillows.com/purchase?url=www.tempurpillows.com/GTWO3"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    ENTER HERE
                                </a>
                            </div>
                        </div>
                    </div>

                    <div className="content-title">
                        <h1 className="content-title__headline">
                            ORDER NUMBER: {props.order_number} |{" "}
                            {props.order_date}
                        </h1>
                        <span className="content-title__info">
                            Need Help?{" "}
                            <a href={props.cs_phone_rfc3966}>
                                {props.cs_phone_national}
                            </a>
                        </span>
                    </div>
                </div>

                <div>
                    {/* order_detail is the pillows shape; the shared template
                        types it as nullable (the cocoon shape lives in a sibling
                        prop), but on pillows it is always populated. */}
                    {props.order_detail && (
                        <OrderDetail data={props.order_detail} />
                    )}
                </div>
            </div>
            <DataLayerPush events={props.ga_events} />
            <GASeller surveyData={props.ga_seller_survey_data} />
        </PageSkeleton>
    );
};
